/**
 * Pure utility functions for drag-select hit detection on charts.
 */

/**
 * Clamps a rectangle to stay within the given bounds.
 *
 * @param {{ x: number, y: number, width: number, height: number }} rect - The rectangle to clamp
 * @param {{ maxWidth: number, maxHeight: number }} bounds - The maximum bounds
 * @returns {{ x: number, y: number, width: number, height: number }} The clamped rectangle
 */
export function clampRect({ x, y, width, height }, { maxWidth, maxHeight }) {
  // Clamp the origin to stay within bounds
  const clampedX = Math.max(0, Math.min(x, maxWidth));
  const clampedY = Math.max(0, Math.min(y, maxHeight));

  // Clamp width and height so the rect doesn't extend past bounds
  const clampedWidth = Math.max(0, Math.min(width, maxWidth - clampedX));
  const clampedHeight = Math.max(0, Math.min(height, maxHeight - clampedY));

  return { x: clampedX, y: clampedY, width: clampedWidth, height: clampedHeight };
}

/**
 * Determines which bar indices fall within a selection rectangle.
 *
 * A bar is selected if its horizontal center falls within the horizontal
 * bounds of the selection rectangle.
 *
 * @param {Object} params
 * @param {{ x: number, y: number, width: number, height: number }} params.selectionRect - The selection rectangle in pixel coordinates
 * @param {number} params.dataLength - Number of bars/categories in the chart
 * @param {{ left: number, right: number, top: number, bottom: number }} params.chartMargin - Chart margin object
 * @param {number} params.containerWidth - Total container width in pixels
 * @returns {number[]} Array of selected bar indices
 */
export function getSelectedBarIndices({ selectionRect, dataLength, chartMargin, containerWidth }) {
  if (!selectionRect || dataLength <= 0 || containerWidth <= 0) {
    return [];
  }

  const plotWidth = containerWidth - chartMargin.left - chartMargin.right;
  if (plotWidth <= 0) return [];

  const categoryWidth = plotWidth / dataLength;

  const selLeft = selectionRect.x;
  const selRight = selectionRect.x + selectionRect.width;

  const indices = [];

  for (let i = 0; i < dataLength; i++) {
    const barCenterX = chartMargin.left + (i + 0.5) * categoryWidth;

    if (barCenterX >= selLeft && barCenterX <= selRight) {
      indices.push(i);
    }
  }

  return indices;
}

/**
 * Determines which scatter point indices fall within a selection rectangle.
 *
 * Each data point's x/y values are mapped to pixel positions using linear
 * interpolation between the domain and the chart pixel range. A point is
 * selected if its pixel position falls within the selection rectangle.
 *
 * @param {Object} params
 * @param {{ x: number, y: number, width: number, height: number }} params.selectionRect - The selection rectangle in pixel coordinates
 * @param {Array} params.data - The data array
 * @param {{ left: number, right: number, top: number, bottom: number }} params.chartMargin - Chart margin object
 * @param {number} params.containerWidth - Total container width in pixels
 * @param {number} params.containerHeight - Total container height in pixels
 * @param {string} params.xAccessor - Key to access x value from each data item
 * @param {string} params.yAccessor - Key to access y value from each data item
 * @param {[number, number]} params.xDomain - [min, max] of the x-axis domain
 * @param {[number, number]} params.yDomain - [min, max] of the y-axis domain
 * @returns {number[]} Array of selected data point indices
 */
export function getSelectedScatterIndices({
  selectionRect,
  data,
  chartMargin,
  containerWidth,
  containerHeight,
  xAccessor,
  yAccessor,
  xDomain,
  yDomain,
}) {
  if (!selectionRect || !data || data.length === 0) {
    return [];
  }

  const plotWidth = containerWidth - chartMargin.left - chartMargin.right;
  const plotHeight = containerHeight - chartMargin.top - chartMargin.bottom;

  if (plotWidth <= 0 || plotHeight <= 0) return [];

  const [xMin, xMax] = xDomain;
  const [yMin, yMax] = yDomain;

  const xRange = xMax - xMin;
  const yRange = yMax - yMin;

  // Avoid division by zero for single-value domains
  if (xRange === 0 && yRange === 0) return [];

  const selLeft = selectionRect.x;
  const selRight = selectionRect.x + selectionRect.width;
  const selTop = selectionRect.y;
  const selBottom = selectionRect.y + selectionRect.height;

  const indices = [];

  for (let i = 0; i < data.length; i++) {
    const point = data[i];
    const xVal = point[xAccessor];
    const yVal = point[yAccessor];

    if (xVal == null || yVal == null) continue;

    // Linear interpolation: map data value to pixel position
    // x: left of plot area → right of plot area
    const pixelX = xRange === 0
      ? chartMargin.left + plotWidth / 2
      : chartMargin.left + ((xVal - xMin) / xRange) * plotWidth;

    // y: note that pixel y is inverted (top of plot = yMax, bottom of plot = yMin)
    const pixelY = yRange === 0
      ? chartMargin.top + plotHeight / 2
      : chartMargin.top + ((yMax - yVal) / yRange) * plotHeight;

    if (pixelX >= selLeft && pixelX <= selRight && pixelY >= selTop && pixelY <= selBottom) {
      indices.push(i);
    }
  }

  return indices;
}
